import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { extractErrorMessage } from "../../api/client";
import { getHandoff, SearchEnvHandoff } from "../../api/searchEnvs";
import { renderHandoffPdf, renderHandoffText } from "./handoffRender";

// Customer-handoff section on the env detail card. Renders nothing
// until the env reaches a state where credentials + endpoint are
// populated (the operator 400s in that case and we hide rather than
// confuse).
//
// Two primary actions:
//   - "Copy email text" → renders the JSON to plain-text and writes
//     it to the clipboard. Paim user pastes into the email they're
//     sending to the customer.
//   - "Download PDF" → renders the JSON to a PDF via jspdf and
//     triggers a download. Same content, different medium.
//
// Optional preview accordion shows the text version inline so the
// paim user can sanity-check before sending.

interface Props {
  envId: string;
  enabled: boolean;
}

export function SearchEnvHandoffPanel({ envId, enabled }: Props) {
  const [handoff, setHandoff] = useState<SearchEnvHandoff | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    getHandoff(envId)
      .then((h) => {
        if (!cancelled) setHandoff(h);
      })
      .catch((err) => {
        if (!cancelled) setError(extractErrorMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, [envId, enabled]);

  const handleCopy = useCallback(async () => {
    if (!handoff) return;
    const text = renderHandoffText(handoff);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }, [handoff]);

  const handlePdf = useCallback(() => {
    if (!handoff) return;
    const doc = renderHandoffPdf(handoff);
    doc.save(`paim-handoff-${handoff.env_id}.pdf`);
  }, [handoff]);

  if (!enabled) return null;
  if (error)
    return (
      <Box
        sx={{
          p: 1.5,
          borderRadius: 1,
          backgroundColor: "background.default",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          Customer handoff
        </Typography>
        <Alert severity="warning" sx={{ mt: 1 }}>
          {error}
        </Alert>
      </Box>
    );
  if (!handoff) return null;

  const previewText = renderHandoffText(handoff);

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        backgroundColor: "background.default",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        rowGap={1}
      >
        <Typography variant="caption" color="text.secondary">
          Customer handoff
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title={copied ? "Copied" : "Copy email-ready text"}>
            <Button
              size="small"
              startIcon={<ContentCopyIcon fontSize="small" />}
              onClick={handleCopy}
            >
              {copied ? "Copied" : "Copy email text"}
            </Button>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon fontSize="small" />}
            onClick={handlePdf}
          >
            Download PDF
          </Button>
          <IconButton
            size="small"
            onClick={() => setShowPreview((v) => !v)}
            aria-label={showPreview ? "Hide preview" : "Show preview"}
          >
            {showPreview ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Stack>
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 0.5 }}
      >
        Send the customer the credentials + URLs + sample queries below. The
        same content goes in both formats — pick whichever your email client
        handles best.
      </Typography>
      <Collapse in={showPreview}>
        <Box
          component="pre"
          sx={{
            mt: 1,
            p: 1.5,
            borderRadius: 1,
            backgroundColor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            fontFamily: "monospace",
            fontSize: 11,
            whiteSpace: "pre-wrap",
            maxHeight: 400,
            overflow: "auto",
          }}
        >
          {previewText}
        </Box>
      </Collapse>
    </Box>
  );
}
